/**
 * What depends on an ENVIRONMENT's values beyond its configuration: the
 * fields that mean nothing for it (`fields[].when`) and the features it does
 * not have (`spec.features` entry `{ id, when, reason }`) — platform-catalog
 * `contracts/apptype.schema.json`. Wagtail with its media on S3
 * (`mediaS3`, not configurable): no media volume to size, no file upload
 * into a pod.
 *
 * OVER EVERY FIELD VALUE, the non-configurable ones included: the caller
 * merges them (`{ ...inputs, ...configuration }`), the light version of an
 * ephemeral branch applied when it is one.
 */

import {
  type AppTypeDescriptor,
  type DescriptorField,
  type FieldValue,
} from './descriptor';
import { evaluate, renderTemplate, truthy } from './expression';

type Values = Readonly<Record<string, FieldValue | undefined>>;

/** Only the values set: `evaluate` reads defined values. */
function defined(values: Values): Record<string, FieldValue> {
  const out: Record<string, FieldValue> = {};
  for (const [id, value] of Object.entries(values)) {
    if (value !== undefined) out[id] = value;
  }
  return out;
}

/**
 * Whether `field` is shown for an environment of `values`: no `when`, or a
 * true one. A `when` that cannot be evaluated (a value it reads is missing,
 * a form still being filled) shows the field: hiding one by mistake would
 * leave a value nobody can see.
 */
export function fieldShown(field: DescriptorField, values: Values): boolean {
  if (!field.when) return true;
  try {
    return truthy(evaluate(field.when, { values: defined(values) }));
  } catch {
    // Unreadable: shown (above).
    return true;
  }
}

/** The configurable fields `fieldShown` hides for `values`. */
export function hiddenFieldIds(
  descriptor: AppTypeDescriptor,
  values: Values,
): Set<string> {
  return new Set(
    descriptor.configurableFields
      .filter(field => !fieldShown(field, values))
      .map(field => field.id),
  );
}

/**
 * Whether an environment of `values` has `feature`: declared by the type,
 * and its `when`, if any, true. Throws, as the quota's expressions do, when
 * the `when` reads a value `values` does not hold: the caller decides (the
 * quota fails, a screen keeps the feature).
 */
export function featureEnabled(
  descriptor: AppTypeDescriptor,
  feature: string,
  values: Values,
): boolean {
  if (!descriptor.features.has(feature)) return false;
  const condition = descriptor.featureConditions.get(feature);
  return (
    !condition || truthy(evaluate(condition.when, { values: defined(values) }))
  );
}

/**
 * The descriptor's reason (`reason`, French) why `feature` is off for an
 * environment of `values`, rendered; undefined without one. Throws as
 * `featureEnabled`.
 */
export function featureOffReason(
  descriptor: AppTypeDescriptor,
  feature: string,
  values: Values,
): string | undefined {
  const reason = descriptor.featureConditions.get(feature)?.reason;
  return reason
    ? renderTemplate(reason, { values: defined(values) })
    : undefined;
}
